export const siteUrl = import.meta.env.VITE_SITE_URL || "https://expertadvise.in";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ExpertAdvise.in",
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    sameAs: [
      "https://facebook.com/expertadvise",
      "https://twitter.com/expertadvise",
      "https://linkedin.com/company/expertadvise",
      "https://instagram.com/expertadvise",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+91-99993-65655",
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    name: "ExpertAdvise.in",
    image: `${siteUrl}/logo.png`,
    url: siteUrl,
    telephone: "+91-99993-65655",
    email: "info@expertadvise.in",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Sector 18",
      addressLocality: "Noida",
      addressRegion: "Uttar Pradesh",
      postalCode: "201301",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "28.5703",
      longitude: "77.3258",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "20:00",
      },
    ],
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  };
}

export function faqPageSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function webpageSchema(title: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    publisher: {
      "@type": "Organization",
      name: "ExpertAdvise.in",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/logo.png`,
      },
    },
  };
}

export interface AuthorSchemaInput {
  name: string;
  jobTitle: string;
  description: string;
  image: string;
  sameAs: string[];
  knowsAbout: string[];
}

export function personSchema(author: AuthorSchemaInput, authorPageUrl?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    jobTitle: author.jobTitle,
    description: author.description,
    image: author.image,
    url: authorPageUrl || `${siteUrl}/blog`,
    sameAs: author.sameAs,
    knowsAbout: author.knowsAbout,
    worksFor: {
      "@type": "Organization",
      name: "ExpertAdvise.in",
      url: siteUrl,
    },
  };
}

export function articleSchema(
  headline: string,
  description: string,
  url: string,
  image: string,
  author: AuthorSchemaInput,
  datePublished: string,
  dateModified: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    image,
    author: personSchema(author, `${siteUrl}/blog`),
    publisher: {
      "@type": "Organization",
      name: "ExpertAdvise.in",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/logo.png`,
      },
    },
    datePublished,
    dateModified,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };
}

export function reviewSchema(reviews: { author: string; rating: number; reviewBody: string; datePublished: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ExpertAdvise.in",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: String(reviews.length),
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
      },
      reviewBody: r.reviewBody,
        datePublished: r.datePublished,
    })),
  };
}

export interface TestimonialSchemaInput {
  name: string;
  rating: number;
  reviewBody: string;
  datePublished: string;
}

export function serviceReviewSchema(
  serviceName: string,
  serviceUrl: string,
  reviews: TestimonialSchemaInput[]
) {
  const avgRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : "4.8";
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    url: serviceUrl,
    provider: organizationSchema(),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount: String(reviews.length || 6),
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
        worstRating: "1",
      },
      reviewBody: r.reviewBody,
      datePublished: r.datePublished,
    })),
  };
}